import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, PackageCheck, RefreshCw, Send, Truck, X } from 'lucide-react';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { useCurrency } from '@/hooks/useCurrency';
import { warehouseService, type Warehouse } from '@/services/warehouseService';
import {
  networkReplenishmentService,
  type NetworkReplenishmentDashboardKpis,
  type StockTransferDetail,
  type StockTransferItem,
  type StockTransferListItem,
} from '@/services/networkReplenishmentService';
import { NETWORK_REPLENISHMENT_REFRESH_EVENT } from '@/utils/networkReplenishmentRefresh';
import {
  NetworkReplenishmentKpiCards,
  type NetworkKpiFilterKey,
} from './NetworkReplenishmentKpiCards';
import { TransferLineExplainPanel } from './TransferLineExplainPanel';

const STATUS_FILTERS = [
  '',
  'PENDING_APPROVAL',
  'APPROVED',
  'ERP_ERROR',
  'SENT_TO_ERP',
  'SHIPPED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CANCELLED',
] as const;

function qty(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-ES', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function daysCover(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('es-ES', { maximumFractionDigits: 1 })}d`;
}

function LineExceptionBadge({
  badge,
  label,
  hint,
}: {
  badge: string | null | undefined;
  label: string;
  hint?: string;
}) {
  if (!badge) return <span className="text-gray-300">·</span>;
  const styles: Record<string, string> = {
    STOCKOUT: 'bg-red-100 text-red-800',
    CUT: 'bg-orange-100 text-orange-800',
    EXCESS: 'bg-amber-100 text-amber-800',
    EDITED: 'bg-blue-100 text-blue-800',
  };
  return (
    <span
      className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles[badge] || 'bg-gray-100 text-gray-700'}`}
      title={hint || label}
    >
      {label}
    </span>
  );
}

function qtyInput(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '0';
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return '0';
  return (Math.round(n * 100) / 100).toFixed(2);
}

const primaryBtn =
  'inline-flex items-center gap-1.5 rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60';
const secondaryBtn =
  'inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60';
const dangerBtn =
  'inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60';

export function NetworkReplenishmentTray() {
  const { t } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [stores, setStores] = useState<Warehouse[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING_APPROVAL');
  const [storeFilter, setStoreFilter] = useState<number | ''>('');
  const [kpiFilter, setKpiFilter] = useState<NetworkKpiFilterKey>('to_review');
  const [kpis, setKpis] = useState<NetworkReplenishmentDashboardKpis | null>(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [transfers, setTransfers] = useState<StockTransferListItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkStoreIds, setBulkStoreIds] = useState<Set<number>>(new Set());
  const [showStoreBulk, setShowStoreBulk] = useState(false);
  const [storeSearch, setStoreSearch] = useState('');
  const [detail, setDetail] = useState<StockTransferDetail | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [editQty, setEditQty] = useState<Record<number, string>>({});

  const loadKpis = useCallback(async () => {
    try {
      setKpisLoading(true);
      const data = await networkReplenishmentService.getDashboardKpis();
      setKpis(data);
    } catch {
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  }, []);

  const listQuery = useMemo(() => {
    const params: { status?: string; destination_warehouse?: number } = {};
    if (storeFilter) params.destination_warehouse = storeFilter;
    if (kpiFilter === 'to_review') params.status = 'PENDING_APPROVAL';
    else if (kpiFilter === 'approved_today') params.status = 'APPROVED';
    else if (kpiFilter === 'sent_today') params.status = 'SENT_TO_ERP';
    else if (kpiFilter === 'errors') params.status = 'ERP_ERROR';
    else if (kpiFilter === 'upcoming') params.status = 'APPROVED';
    else if (statusFilter) params.status = statusFilter;
    return params;
  }, [storeFilter, kpiFilter, statusFilter]);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [allWh, list] = await Promise.all([
        warehouseService.getWarehouses({ is_active: true }),
        networkReplenishmentService.getStockTransfers(listQuery),
      ]);
      setStores(
        allWh.filter(
          (w) => w.warehouse_type === 'BRANCH' || w.warehouse_type === 'STORE',
        ),
      );
      let rows = list;
      if (kpiFilter === 'upcoming') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const horizon = new Date(today);
        horizon.setDate(horizon.getDate() + 7);
        rows = list.filter((row) => {
          if (!row.planned_shipping_date) return false;
          const d = new Date(row.planned_shipping_date);
          return d >= today && d <= horizon;
        });
      } else if (kpiFilter === 'approved_today' || kpiFilter === 'sent_today') {
        const todayIso = new Date().toISOString().slice(0, 10);
        rows = list.filter((row) => {
          if (kpiFilter === 'approved_today') {
            return row.approved_at?.slice(0, 10) === todayIso;
          }
          return true;
        });
      }
      setTransfers(rows);
      setSelectedIds((prev) => {
        const visible = new Set(rows.map((r) => r.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('networkReplenishment.loadError'));
    } finally {
      setLoading(false);
    }
  }, [listQuery, kpiFilter, t]);

  const pendingTransfers = useMemo(
    () =>
      transfers.filter(
        (row) => row.status === 'PENDING_APPROVAL' || row.status === 'SUGGESTED',
      ),
    [transfers],
  );

  const allPendingSelected =
    pendingTransfers.length > 0 &&
    pendingTransfers.every((row) => selectedIds.has(row.id));

  const toggleSelect = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleSelectAllPending = (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(pendingTransfers.map((row) => row.id)));
  };

  const filteredBulkStores = useMemo(() => {
    const q = storeSearch.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter(
      (s) =>
        s.code.toLowerCase().includes(q) ||
        (s.name || '').toLowerCase().includes(q),
    );
  }, [stores, storeSearch]);

  const allFilteredStoresSelected =
    filteredBulkStores.length > 0 &&
    filteredBulkStores.every((s) => bulkStoreIds.has(s.id));

  const runBulkApprove = async (body: {
    transfer_ids?: number[];
    destination_warehouse_ids?: number[];
    approve_all_pending?: boolean;
  }) => {
    try {
      setBusy(true);
      setError(null);
      setMessage(null);
      const result = await networkReplenishmentService.bulkApproveStockTransfers({
        ...body,
        notes: t('networkReplenishment.tray.bulkApproveNote'),
      });
      setSelectedIds(new Set());
      setBulkStoreIds(new Set());
      setShowStoreBulk(false);
      setStoreSearch('');
      setMessage(
        t('networkReplenishment.tray.bulkResult', {
          approved: result.approved_count,
          failed: result.failed_count,
        }),
      );
      if (result.failed_count > 0) {
        const firstFail = result.results.find((r) => !r.ok);
        if (firstFail?.detail) {
          setError(
            typeof firstFail.detail === 'string'
              ? firstFail.detail
              : t('networkReplenishment.tray.bulkPartialFail'),
          );
        }
      }
      await Promise.all([loadList(), loadKpis()]);
      if (selectedId != null) void loadDetail(selectedId);
    } catch (err) {
      const apiErr = err as { message?: string; detail?: string };
      setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const handleBulkApproveSelected = async () => {
    const ids = [...selectedIds].filter((id) =>
      pendingTransfers.some((row) => row.id === id),
    );
    if (ids.length === 0) {
      setError(t('networkReplenishment.tray.bulkNoneSelected'));
      return;
    }
    if (!window.confirm(t('networkReplenishment.tray.bulkConfirm', { count: ids.length }))) {
      return;
    }
    await runBulkApprove({ transfer_ids: ids });
  };

  const handleBulkApproveByStores = async () => {
    const destIds = [...bulkStoreIds];
    if (destIds.length === 0) {
      setError(t('networkReplenishment.tray.bulkNoStores'));
      return;
    }
    if (
      !window.confirm(
        t('networkReplenishment.tray.bulkConfirmStores', { count: destIds.length }),
      )
    ) {
      return;
    }
    await runBulkApprove({ destination_warehouse_ids: destIds });
  };

  const handleBulkApproveAllPending = async () => {
    if (!window.confirm(t('networkReplenishment.tray.bulkConfirmAll'))) {
      return;
    }
    await runBulkApprove({ approve_all_pending: true });
  };

  const toggleSelectAllFilteredStores = (checked: boolean) => {
    setBulkStoreIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredBulkStores.forEach((s) => next.add(s.id));
      } else {
        filteredBulkStores.forEach((s) => next.delete(s.id));
      }
      return next;
    });
  };

  const loadDetail = useCallback(
    async (id: number) => {
      try {
        setBusy(true);
        setError(null);
        const data = await networkReplenishmentService.getStockTransfer(id);
        setDetail(data);
        setSelectedId(id);
        const visible = data.items.filter((item) => {
          const suggested = Number(item.adjusted_quantity ?? item.allocated_quantity ?? 0);
          return Number.isFinite(suggested) && suggested > 0;
        });
        setSelectedLineId(visible[0]?.id ?? null);
        const draft: Record<number, string> = {};
        data.items.forEach((item) => {
          draft[item.id] = qtyInput(
            item.approved_quantity ?? item.allocated_quantity ?? item.requested_quantity ?? '0',
          );
        });
        setEditQty(draft);
      } catch (err) {
        const apiErr = err as { message?: string };
        setError(apiErr?.message || t('networkReplenishment.loadError'));
      } finally {
        setBusy(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadKpis();
  }, [loadKpis]);

  useEffect(() => {
    const onRefresh = () => {
      void loadList();
      void loadKpis();
      if (selectedId != null) void loadDetail(selectedId);
    };
    window.addEventListener(NETWORK_REPLENISHMENT_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(NETWORK_REPLENISHMENT_REFRESH_EVENT, onRefresh);
  }, [loadList, loadKpis, loadDetail, selectedId]);

  const handleKpiFilter = (key: NetworkKpiFilterKey) => {
    setKpiFilter(key);
    if (key === 'to_review') setStatusFilter('PENDING_APPROVAL');
    else if (key === 'approved_today' || key === 'upcoming') setStatusFilter('APPROVED');
    else if (key === 'sent_today') setStatusFilter('SENT_TO_ERP');
    else if (key === 'errors') setStatusFilter('ERP_ERROR');
    else setStatusFilter('');
  };

  const canAct = useMemo(
    () => detail?.status === 'PENDING_APPROVAL' || detail?.status === 'SUGGESTED',
    [detail],
  );

  const handleSaveLine = async (itemId: number) => {
    if (!detail || !canAct) return;
    try {
      setBusy(true);
      setError(null);
      setMessage(null);
      await networkReplenishmentService.editTransferItem(
        itemId,
        editQty[itemId] ?? '0',
        t('networkReplenishment.tray.editReason'),
      );
      setMessage(t('networkReplenishment.tray.lineSaved'));
      await loadDetail(detail.id);
      await Promise.all([loadList(), loadKpis()]);
    } catch (err) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    if (!detail || !canAct) return;
    try {
      setBusy(true);
      setError(null);
      setMessage(null);
      const item_quantities: Record<number, string> = {};
      detail.items.forEach((item) => {
        item_quantities[item.id] = editQty[item.id] ?? '0';
      });
      const updated = await networkReplenishmentService.approveStockTransfer(detail.id, {
        item_quantities,
        notes: t('networkReplenishment.tray.approveNote'),
      });
      setDetail(updated);
      setMessage(t('networkReplenishment.tray.approved'));
      await Promise.all([loadList(), loadKpis()]);
    } catch (err) {
      const apiErr = err as { message?: string; detail?: string };
      setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!detail) return;
    if (
      detail.status === 'SHIPPED' ||
      detail.status === 'RECEIVED' ||
      detail.status === 'PARTIALLY_RECEIVED' ||
      detail.status === 'SENT_TO_ERP'
    ) {
      return;
    }
    try {
      setBusy(true);
      setError(null);
      setMessage(null);
      const updated = await networkReplenishmentService.cancelStockTransfer(
        detail.id,
        t('networkReplenishment.tray.cancelReason'),
      );
      setDetail(updated);
      setMessage(t('networkReplenishment.tray.cancelled'));
      await Promise.all([loadList(), loadKpis()]);
    } catch (err) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const runLifecycle = async (
    action: () => Promise<StockTransferDetail>,
    successKey: string,
  ) => {
    try {
      setBusy(true);
      setError(null);
      setMessage(null);
      const updated = await action();
      setDetail(updated);
      setMessage(t(successKey));
      await Promise.all([loadList(), loadKpis()]);
    } catch (err) {
      const apiErr = err as { message?: string; detail?: string };
      setError(apiErr?.detail || apiErr?.message || t('networkReplenishment.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = useCallback(
    (status: string) =>
      t(`networkReplenishment.tray.statuses.${status}`, { defaultValue: status }),
    [t],
  );

  const listColumns: Column[] = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
            checked={allPendingSelected}
            disabled={pendingTransfers.length === 0}
            onChange={(e) => toggleSelectAllPending(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            title={t('networkReplenishment.tray.selectAllPending')}
            aria-label={t('networkReplenishment.tray.selectAllPending')}
          />
        ),
        accessor: 'id',
        cell: (_value, row: StockTransferListItem) => {
          const pending =
            row.status === 'PENDING_APPROVAL' || row.status === 'SUGGESTED';
          return (
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary disabled:opacity-40"
              checked={selectedIds.has(row.id)}
              disabled={!pending}
              onChange={(e) => toggleSelect(row.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              aria-label={t('networkReplenishment.tray.selectRow')}
            />
          );
        },
      },
      {
        header: t('networkReplenishment.tray.transferNumber'),
        accessor: 'transfer_number',
        sortable: true,
        cell: (value) => <span className="font-medium text-gray-900">{String(value)}</span>,
      },
      {
        header: t('networkReplenishment.lanes.destination'),
        accessor: 'destination_warehouse_code',
        sortable: true,
      },
      {
        header: t('networkReplenishment.tray.status'),
        accessor: 'status',
        sortable: true,
        cell: (value) => statusLabel(String(value)),
      },
      {
        header: t('networkReplenishment.lanes.nextOrder'),
        accessor: 'order_date',
        sortable: true,
      },
      {
        header: t('networkReplenishment.tray.productsCount'),
        accessor: 'items_count',
        sortable: true,
        align: 'right',
        numeric: true,
      },
      {
        header: t('networkReplenishment.tray.quantity'),
        accessor: 'total_quantity',
        sortable: true,
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.estimatedValue'),
        accessor: 'total_value',
        sortable: true,
        align: 'right',
        numeric: true,
        cell: (value) =>
          value === null || value === undefined || value === ''
            ? '—'
            : formatMoney(value),
      },
    ],
    [t, statusLabel, allPendingSelected, pendingTransfers.length, selectedIds, formatMoney],
  );

  const visibleItems = useMemo(
    () =>
      (detail?.items ?? []).filter((item) => {
        const suggested = Number(item.adjusted_quantity ?? item.allocated_quantity ?? 0);
        return Number.isFinite(suggested) && suggested > 0;
      }),
    [detail],
  );

  const selectedLine = useMemo(
    () => visibleItems.find((item) => item.id === selectedLineId) ?? null,
    [visibleItems, selectedLineId],
  );

  const lineColumns: Column[] = useMemo(() => {
    const gapQty = (item: StockTransferItem) => {
      const n = Number(item.unallocated_quantity);
      return Number.isFinite(n) && n > 0 ? qty(item.unallocated_quantity) : '—';
    };
    const cols: Column[] = [
      {
        header: t('networkReplenishment.tray.signal'),
        accessor: 'id',
        cell: (_value, item: StockTransferItem) => {
          const badge = item.review?.exception_badge;
          return (
            <LineExceptionBadge
              badge={badge}
              label={
                badge
                  ? t(`networkReplenishment.tray.badges.${badge}`)
                  : t('networkReplenishment.tray.badges.none')
              }
              hint={
                badge
                  ? t(`networkReplenishment.tray.badgeHints.${badge}`)
                  : undefined
              }
            />
          );
        },
      },
      {
        header: t('networkReplenishment.tray.product'),
        accessor: 'product_code',
        cell: (_value, item: StockTransferItem) => (
          <div>
            <div className="font-medium text-gray-900">{item.product_code}</div>
            <div className="text-xs text-gray-500">
              {item.product_name}
              {item.unit_code ? ` · ${item.unit_code}` : ''}
            </div>
          </div>
        ),
      },
      {
        header: t('networkReplenishment.tray.stockStore'),
        accessor: 'destination_stock_snapshot',
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.inTransit'),
        accessor: 'destination_stock_in_transit_snapshot',
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.coverageNow'),
        accessor: 'review',
        align: 'right',
        numeric: true,
        cell: (_value, item: StockTransferItem) =>
          daysCover(item.review?.days_of_cover_before),
      },
      {
        header: t('networkReplenishment.tray.forecastCycle'),
        accessor: 'forecast_protection_period',
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.centralStock'),
        accessor: 'source_available_stock_snapshot',
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.suggested'),
        accessor: 'adjusted_quantity',
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.allocated'),
        accessor: 'allocated_quantity',
        align: 'right',
        numeric: true,
        cell: (value) => qty(value),
      },
      {
        header: t('networkReplenishment.tray.gap'),
        accessor: 'unallocated_quantity',
        align: 'right',
        numeric: true,
        cell: (_value, item: StockTransferItem) => gapQty(item),
      },
      {
        header: t('networkReplenishment.tray.approveQty'),
        accessor: 'approved_quantity',
        align: 'right',
        cell: (_value, item: StockTransferItem) =>
          canAct ? (
            <input
              className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
              value={editQty[item.id] ?? ''}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                setEditQty((prev) => ({ ...prev, [item.id]: e.target.value }))
              }
            />
          ) : (
            qty(item.approved_quantity ?? item.effective_quantity)
          ),
      },
    ];
    if (canAct) {
      cols.push({
        header: '',
        accessor: 'save',
        cell: (_value, item: StockTransferItem) => (
          <button
            type="button"
            disabled={busy}
            className="text-xs font-medium text-stocker-primary hover:underline disabled:opacity-50"
            onClick={(e) => {
              e.stopPropagation();
              void handleSaveLine(item.id);
            }}
          >
            {t('networkReplenishment.tray.saveLine')}
          </button>
        ),
      });
    }
    return cols;
  }, [t, canAct, editQty, busy]);

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-6 p-4 md:p-6">
      <NetworkReplenishmentKpiCards
        kpis={kpis}
        loading={kpisLoading}
        activeFilter={kpiFilter}
        onFilter={handleKpiFilter}
      />

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            {t('networkReplenishment.tray.status')}
          </span>
          <select
            className="rounded-md border border-gray-300 px-3 py-2 focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
            value={statusFilter}
            onChange={(e) => {
              setKpiFilter(null);
              setStatusFilter(e.target.value);
            }}
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s || 'all'} value={s}>
                {s
                  ? t(`networkReplenishment.tray.statuses.${s}`, { defaultValue: s })
                  : t('networkReplenishment.tray.allStatuses')}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            {t('networkReplenishment.tray.store')}
          </span>
          <select
            className="rounded-md border border-gray-300 px-3 py-2 focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
            value={storeFilter}
            onChange={(e) => {
              setStoreFilter(e.target.value ? Number(e.target.value) : '');
            }}
          >
            <option value="">{t('networkReplenishment.tray.allStores')}</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} — {s.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {message && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {message}
        </div>
      )}

      <section className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-gray-900">
            {t('networkReplenishment.tabs.tray')}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleBulkApproveAllPending()}
              className={primaryBtn}
            >
              <Check className="h-4 w-4" />
              {t('networkReplenishment.tray.bulkApproveAll')}
            </button>
            <button
              type="button"
              disabled={busy || selectedIds.size === 0}
              onClick={() => void handleBulkApproveSelected()}
              className={secondaryBtn}
            >
              {t('networkReplenishment.tray.bulkApproveSelected', {
                count: selectedIds.size,
              })}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowStoreBulk((v) => !v)}
              className={secondaryBtn}
            >
              {t('networkReplenishment.tray.bulkApproveByStores')}
            </button>
          </div>
        </div>

        {showStoreBulk && (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm text-gray-600">
              {t('networkReplenishment.tray.bulkStoresHelp')}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="search"
                className="min-w-[12rem] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
                placeholder={t('networkReplenishment.tray.bulkStoreSearch')}
                value={storeSearch}
                onChange={(e) => setStoreSearch(e.target.value)}
              />
              <button
                type="button"
                className="text-xs font-medium text-stocker-primary hover:underline"
                onClick={() => toggleSelectAllFilteredStores(!allFilteredStoresSelected)}
              >
                {allFilteredStoresSelected
                  ? t('networkReplenishment.tray.bulkClearFiltered')
                  : t('networkReplenishment.tray.bulkSelectFiltered', {
                      count: filteredBulkStores.length,
                    })}
              </button>
              {bulkStoreIds.size > 0 && (
                <button
                  type="button"
                  className="text-xs font-medium text-gray-600 hover:underline"
                  onClick={() => setBulkStoreIds(new Set())}
                >
                  {t('networkReplenishment.tray.bulkClearSelection', {
                    count: bulkStoreIds.size,
                  })}
                </button>
              )}
            </div>
            <div className="max-h-48 divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
              {filteredBulkStores.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-gray-500">
                  {t('networkReplenishment.tray.bulkStoreSearchEmpty')}
                </p>
              ) : (
                filteredBulkStores.map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                      checked={bulkStoreIds.has(s.id)}
                      onChange={(e) => {
                        setBulkStoreIds((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(s.id);
                          else next.delete(s.id);
                          return next;
                        });
                      }}
                    />
                    <span className="min-w-0 truncate">
                      <span className="font-medium">{s.code}</span>
                      <span className="text-gray-500"> — {s.name}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
            <button
              type="button"
              disabled={busy || bulkStoreIds.size === 0}
              onClick={() => void handleBulkApproveByStores()}
              className={primaryBtn}
            >
              <Check className="h-4 w-4" />
              {t('networkReplenishment.tray.bulkApproveStoresAction', {
                count: bulkStoreIds.size,
              })}
            </button>
          </div>
        )}

        <div className="min-w-0 max-w-full">
          <DataTable
            columns={listColumns}
            data={transfers}
            loading={loading}
            pagination
            onRowClick={(item: StockTransferListItem) => void loadDetail(item.id)}
            rowClassName={(item: StockTransferListItem) =>
              selectedId === item.id ? 'bg-stocker-primary/10' : ''
            }
          />
        </div>
        {!loading && transfers.length === 0 && (
          <p className="text-center text-sm text-gray-500">{t('networkReplenishment.tray.empty')}</p>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('networkReplenishment.tray.detailTitle')}
        </h2>
        {!detail && (
          <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            {t('networkReplenishment.tray.selectHint')}
          </p>
        )}
        {detail && (
          <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-gray-900">{detail.transfer_number}</h3>
                <p className="text-sm text-gray-600">
                  {detail.source_warehouse_code} → {detail.destination_warehouse_code} ·{' '}
                  <span className="font-medium">{statusLabel(detail.status)}</span>
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {t('networkReplenishment.tray.orderDate')}: {detail.order_date}
                  {detail.expected_receipt_date
                    ? ` · ${t('networkReplenishment.tray.receipt')}: ${detail.expected_receipt_date}`
                    : ''}
                  {detail.next_receipt_date
                    ? ` · ${t('networkReplenishment.tray.nextCycle')}: ${detail.next_receipt_date}`
                    : ''}
                  {detail.total_value != null && detail.total_value !== ''
                    ? ` · ${formatMoney(detail.total_value)}`
                    : ''}
                </p>
                {detail.external_ref && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('networkReplenishment.tray.externalRef')}: {detail.external_ref}
                  </p>
                )}
                {detail.integration_error && (
                  <p className="mt-1 text-xs text-red-600">{detail.integration_error}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {canAct && (
                  <button type="button" disabled={busy} onClick={() => void handleApprove()} className={primaryBtn}>
                    <Check className="h-4 w-4" />
                    {t('networkReplenishment.tray.approve')}
                  </button>
                )}
                {detail.status === 'APPROVED' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runLifecycle(
                        () => networkReplenishmentService.sendStockTransfer(detail.id),
                        'networkReplenishment.tray.sent',
                      )
                    }
                    className={primaryBtn}
                  >
                    <Send className="h-4 w-4" />
                    {t('networkReplenishment.tray.send')}
                  </button>
                )}
                {detail.status === 'ERP_ERROR' && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runLifecycle(
                        () => networkReplenishmentService.retryStockTransfer(detail.id),
                        'networkReplenishment.tray.sent',
                      )
                    }
                    className={primaryBtn}
                  >
                    <RefreshCw className="h-4 w-4" />
                    {t('networkReplenishment.tray.retry')}
                  </button>
                )}
                {(detail.status === 'SENT_TO_ERP' ||
                  detail.status === 'APPROVED' ||
                  detail.status === 'PREPARING') && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runLifecycle(
                        () => networkReplenishmentService.markShippedStockTransfer(detail.id),
                        'networkReplenishment.tray.shipped',
                      )
                    }
                    className={secondaryBtn}
                  >
                    <Truck className="h-4 w-4" />
                    {t('networkReplenishment.tray.markShipped')}
                  </button>
                )}
                {(detail.status === 'SHIPPED' || detail.status === 'PARTIALLY_RECEIVED') && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void runLifecycle(
                        () => networkReplenishmentService.receiveStockTransfer(detail.id),
                        'networkReplenishment.tray.received',
                      )
                    }
                    className={primaryBtn}
                  >
                    <PackageCheck className="h-4 w-4" />
                    {t('networkReplenishment.tray.receive')}
                  </button>
                )}
                {detail.status !== 'CANCELLED' &&
                  detail.status !== 'SHIPPED' &&
                  detail.status !== 'RECEIVED' &&
                  detail.status !== 'PARTIALLY_RECEIVED' &&
                  detail.status !== 'SENT_TO_ERP' && (
                    <button type="button" disabled={busy} onClick={() => void handleCancel()} className={dangerBtn}>
                      <X className="h-4 w-4" />
                      {t('networkReplenishment.tray.cancel')}
                    </button>
                  )}
              </div>
            </div>

            <DataTable
              columns={lineColumns}
              data={visibleItems}
              loading={busy && detail.items.length === 0}
              pagination={false}
              compact
              onRowClick={(item: StockTransferItem) => setSelectedLineId(item.id)}
              rowClassName={(item: StockTransferItem) =>
                selectedLineId === item.id ? 'bg-stocker-primary/10' : ''
              }
            />

            <p className="text-xs text-gray-500">
              {t('networkReplenishment.tray.badgeLegend')}
            </p>

            {selectedLine && (
              <TransferLineExplainPanel detail={detail} item={selectedLine} />
            )}

            <div>
              <h4 className="text-sm font-semibold text-gray-800">
                {t('networkReplenishment.tray.timeline')}
              </h4>
              <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-gray-600">
                {detail.events.map((ev) => (
                  <li key={ev.id}>
                    <span className="font-medium">{ev.event_type}</span>
                    {ev.user_username ? ` · ${ev.user_username}` : ''} ·{' '}
                    {new Date(ev.created_at).toLocaleString()}
                    {ev.message ? ` — ${ev.message}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
